/**
 * Cadastro Comercial — endpoints expandidos do tenant.
 *
 * Endpoints servidos por routers/tenants_company.py no backend.
 */

import { api } from "./client";
import type {
  AuditLog,
  CepLookup,
  TenantCompany,
  TenantCompanyUpdate,
  TenantCompleteness,
  TenantLicenca,
  TenantLicencaCreate,
  TenantLicencaUpdate,
  TenantResponsavel,
  TenantResponsavelCreate,
  TenantResponsavelUpdate,
} from "@/types";

export const tenantsCompanyApi = {
  // ---------- Company (1:1) ----------
  async getCompany(tenantId: string): Promise<TenantCompany> {
    const { data } = await api.get<TenantCompany>(`/tenants/${tenantId}/company`);
    return data;
  },
  async updateCompany(
    tenantId: string,
    payload: TenantCompanyUpdate,
  ): Promise<TenantCompany> {
    const { data } = await api.patch<TenantCompany>(
      `/tenants/${tenantId}/company`,
      payload,
    );
    return data;
  },

  // ---------- Responsáveis ----------
  async listResponsaveis(
    tenantId: string,
    includeInactive = false,
  ): Promise<TenantResponsavel[]> {
    const { data } = await api.get<TenantResponsavel[]>(
      `/tenants/${tenantId}/responsaveis`,
      { params: { include_inactive: includeInactive } },
    );
    return data;
  },
  async createResponsavel(
    tenantId: string,
    payload: TenantResponsavelCreate,
  ): Promise<TenantResponsavel> {
    const { data } = await api.post<TenantResponsavel>(
      `/tenants/${tenantId}/responsaveis`,
      payload,
    );
    return data;
  },
  async updateResponsavel(
    tenantId: string,
    respId: string,
    payload: TenantResponsavelUpdate,
  ): Promise<TenantResponsavel> {
    const { data } = await api.patch<TenantResponsavel>(
      `/tenants/${tenantId}/responsaveis/${respId}`,
      payload,
    );
    return data;
  },
  async deactivateResponsavel(tenantId: string, respId: string): Promise<void> {
    await api.delete(`/tenants/${tenantId}/responsaveis/${respId}`);
  },

  // ---------- Licenças ----------
  async listLicencas(
    tenantId: string,
    includeInactive = false,
  ): Promise<TenantLicenca[]> {
    const { data } = await api.get<TenantLicenca[]>(
      `/tenants/${tenantId}/licencas`,
      { params: { include_inactive: includeInactive } },
    );
    return data;
  },
  async createLicenca(
    tenantId: string,
    payload: TenantLicencaCreate,
  ): Promise<TenantLicenca> {
    const { data } = await api.post<TenantLicenca>(
      `/tenants/${tenantId}/licencas`,
      payload,
    );
    return data;
  },
  async updateLicenca(
    tenantId: string,
    licId: string,
    payload: TenantLicencaUpdate,
  ): Promise<TenantLicenca> {
    const { data } = await api.patch<TenantLicenca>(
      `/tenants/${tenantId}/licencas/${licId}`,
      payload,
    );
    return data;
  },
  async deactivateLicenca(tenantId: string, licId: string): Promise<void> {
    await api.delete(`/tenants/${tenantId}/licencas/${licId}`);
  },

  // ---------- Logo (multipart upload) ----------
  async uploadLogo(tenantId: string, file: File): Promise<{ logo_path: string }> {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post<{ logo_path: string }>(
      `/tenants/${tenantId}/logo`,
      fd,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return data;
  },
  async deleteLogo(tenantId: string): Promise<void> {
    await api.delete(`/tenants/${tenantId}/logo`);
  },
  // URL pública pra renderizar o logo (passa por axios pra incluir Bearer)
  logoUrl(tenantId: string): string {
    // Backend serve com Cache-Control adequado. Adicionamos timestamp pra
    // evitar cache do browser quando usuário troca o logo.
    return `${api.defaults.baseURL ?? ""}/tenants/${tenantId}/logo?t=${Date.now()}`;
  },

  // ---------- Completeness ----------
  async getCompleteness(tenantId: string): Promise<TenantCompleteness> {
    const { data } = await api.get<TenantCompleteness>(
      `/tenants/${tenantId}/completeness`,
    );
    return data;
  },

  // ---------- ViaCEP ----------
  async lookupCep(cep: string): Promise<CepLookup> {
    const cleaned = cep.replace(/\D/g, "");
    const { data } = await api.get<CepLookup>(`/viacep/${cleaned}`);
    return data;
  },

  // ---------- Audit logs ----------
  async listAuditLogs(tenantId: string, limit = 100): Promise<AuditLog[]> {
    const { data } = await api.get<AuditLog[]>(
      `/tenants/${tenantId}/audit-logs`,
      { params: { limit } },
    );
    return data;
  },
};
